import { downloadTextFile, escapeCsvValue } from './csv';
import { formatTaskStatus, formatTaskPriority } from './taskLabels';
import { formatDateTime } from './locale';
import { buildFortnoxSelectionExport } from './taskBilling';
import { formatWeekdayList, normalizeWeekdays } from './recurrenceWeekdays';

const formatExportDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return formatDateTime(dateStr);
  } catch {
    return dateStr;
  }
};

const checklistToText = (checklist) => {
  if (!checklist || !Array.isArray(checklist)) {
    return '';
  }
  return checklist
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      if (item && typeof item === 'object') {
        return item.text || '';
      }
      return String(item || '');
    })
    .filter((item) => item !== '')
    .join('; ');
};

/** Build CSV string for selected tasks (`t` must resolve `export:` and `tasks:` keys). */
export const buildTasksCsvContent = (tasks, t) => {
  const headers = [
    t('export:csvId'),
    t('export:csvTitle'),
    t('export:csvDescription'),
    t('export:csvStatus'),
    t('export:csvPriority'),
    t('export:csvEstimatedDuration'),
    t('export:csvLocation'),
    t('export:csvLocationName'),
    t('export:csvCompany'),
    t('export:csvCreatedBy'),
    t('export:csvAssignedTo'),
    t('export:csvScheduledFor'),
    t('export:csvRecurrenceEnabled'),
    t('export:csvRecurrenceInterval'),
    t('export:csvRecurrenceWeekdays'),
    t('export:csvCreatedAt'),
    t('export:csvUpdatedAt'),
    t('export:csvChecklistItems'),
    t('export:csvAttested'),
    t('export:csvInvoiced'),
    t('export:csvAttestedAt'),
    t('export:csvInvoicedAt')
  ];

  const rows = tasks.map((task) => [
    task.id || '',
    task.title || '',
    (task.description || '').replace(/\n/g, ' ').replace(/,/g, ';'),
    formatTaskStatus(task.status, t) || '',
    formatTaskPriority(task.priority, t) || '',
    task.estimated_duration || '',
    task.location || '',
    task.location_name || '',
    task.company_name || '',
    task.created_by_name || '',
    task.assigned_user_names || '',
    task.scheduled_for ? formatExportDateTime(task.scheduled_for) : '',
    task.recurrence_enabled ? t('export:csvYes') : t('export:csvNo'),
    task.recurrence_interval_days || '',
    formatWeekdayList(normalizeWeekdays(task.recurrence_weekdays), t),
    formatExportDateTime(task.created_at),
    formatExportDateTime(task.updated_at),
    checklistToText(task.checklist),
    task.attested ? t('export:csvYes') : t('export:csvNo'),
    task.invoiced ? t('export:csvYes') : t('export:csvNo'),
    formatExportDateTime(task.attested_at),
    formatExportDateTime(task.invoiced_at)
  ]);

  return [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => row.map(escapeCsvValue).join(','))
  ].join('\n');
};

export const downloadTasksCsv = (tasks, t) => {
  downloadTextFile(
    `tasks_export_${new Date().toISOString().split('T')[0]}.csv`,
    buildTasksCsvContent(tasks, t),
    'text/csv;charset=utf-8;'
  );
};

export const downloadTasksJson = (tasks) => {
  const payload = {
    format: 'serviceinmind-tasks-export',
    exportedAt: new Date().toISOString(),
    tasks: tasks.map((task) => ({
      id: task.id,
      title: task.title,
      description: task.description || null,
      status: task.status,
      priority: task.priority,
      location_id: task.location_id || null,
      location_name: task.location_name || task.location || null,
      assigned_user_names: task.assigned_user_names || null,
      scheduled_for: task.scheduled_for || null,
      recurrence_enabled: Boolean(task.recurrence_enabled),
      recurrence_interval_days: task.recurrence_interval_days || null,
      recurrence_weekdays: normalizeWeekdays(task.recurrence_weekdays),
      created_at: task.created_at || null,
      updated_at: task.updated_at || null,
      attested: Boolean(task.attested),
      invoiced: Boolean(task.invoiced),
      attested_at: task.attested_at || null,
      invoiced_at: task.invoiced_at || null
    }))
  };
  downloadTextFile(
    `tasks_export_${new Date().toISOString().split('T')[0]}.json`,
    `${JSON.stringify(payload, null, 2)}\n`,
    'application/json;charset=utf-8;'
  );
};

export const downloadFortnoxSelectionJson = (tasks) => {
  const payload = buildFortnoxSelectionExport(tasks);
  downloadTextFile(
    `fortnox_invoice_selection_${new Date().toISOString().split('T')[0]}.json`,
    `${JSON.stringify(payload, null, 2)}\n`,
    'application/json;charset=utf-8;'
  );
};
